import React from 'react';
import { isEmpty, sortBy, keyBy } from 'lodash';
import ellipsis from 'text-ellipsis';
import { toast } from 'react-toastify';

import firebase, { functions } from '../../firebase';
import { canUpdateUserTagRules } from '../../shared/abilities';
import AdminPage from '../hocs/AdminPage';
import UserTagRuleFormModal from '../modals/UserTagRuleFormModal';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useQueryParams from '../hooks/useQueryParams';
import AddInTenantButton from '../AddInTenantButton';
import ProgressButton from '../ProgressButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import QuerySelector from '../QuerySelector';
import ModalButton from '../ModalButton';
import ModelFormModal from '../modals/ModelFormModal';
import { ruleTypes, operators } from '../../shared/models/userTagRule';
import { fields as groupFields } from '../../shared/models/userTagRuleGroup';
import { defaultGroups } from '../../shared/models/surveyGroup';

const db = firebase.firestore();
const applyUserTagRulesToAllUsers = functions.httpsCallable('applyUserTagRulesToAllUsers', { timeout: 550000 });
const questionsRef = db.collection('questions');
const userTagRulesRef = db.collection('userTagRules');

export default AdminPage(function AdminUserTagRules(props) {
  const { user, tenant, } = props;
  const { groups: groupsForFilter } = useQueryParams();
  const events = useCollectionSubscriptionInTenant(db.collection('events'));
  const eventsById = keyBy(events, 'id');
  const userTags = useCollectionSubscriptionInTenant(db.collection('userTags'));
  const userTagsById = keyBy(userTags, 'id');
  const questions = useCollectionSubscriptionInTenant(questionsRef);
  const questionsById = keyBy(questions, 'id');
  const _surveyGroups = useCollectionSubscriptionInTenant(db.collection('surveyGroups'));
  const surveyGroups = [...defaultGroups, ..._surveyGroups];
  const userTagRules = sortBy(useCollectionSubscriptionInTenant(userTagRulesRef), _ => _.createdAt.toDate());
  const userTagRuleGroups = sortBy(useCollectionSubscriptionInTenant(db.collection('userTagRuleGroups')), (_) =>
    _.createdAt.toDate()
  );
  const userTagRuleGroupsById = keyBy(userTagRuleGroups, 'id');
  const groupOptions = userTagRuleGroups.map((_) => ({ label: _.name, value: _.id }));

  let filteredUserTagRules = userTagRules;
  if (!isEmpty(groupsForFilter)) {
    filteredUserTagRules = filteredUserTagRules.filter((_) =>
      groupsForFilter.some((group) => (_?.userTagRuleGroupIds ?? []).includes(group))
    );
  }

  const onClickApplyAllUsers = async () => {
    if(!window.confirm('過去データに適用します。よろしいですか？'));

    try {
      await applyUserTagRulesToAllUsers({ tenantId: tenant.id, });
      toast.success('過去データに適用しました');
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  return (
    <div>
      <div className="admin-question-sets container-fluid py-5 position-relative">
        <div className="d-flex justify-content-center mb-3">
          <h4>ユーザータグルール一覧</h4>
        </div>
        <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
          <QuerySelector
            paramName="groups"
            className="ml-0"
            width={400}
            isMulti
            options={groupOptions}
            label="グループで絞込み"
          />
          <div className="d-flex justify-content-end align-items-end gap-2">
            <ModalButton
              title="グループ管理"
              content={(_) => <Groups user={user} userTagRuleGroups={userTagRuleGroups} />}
              modalProps={{ style: { minWidth: 1300 } }}
            >
              グループ管理
            </ModalButton>
            <ProgressButton process={onClickApplyAllUsers}>
              過去データに適用する
            </ProgressButton>
            <AddInTenantButton
              itemRef={userTagRulesRef.doc()}
              FormModal={UserTagRuleFormModal}
              disabled={!canUpdateUserTagRules(user)}
              formProps={{ surveyGroups, events, userTags, userTagRuleGroups, }}
            />
          </div>
        </div>
        <div>
          {userTagRules.length > 0 ? (
            <table className="table">
              <thead className="thead-light text-center">
                <tr>
                  <th style={{ minWidth: 200 }}>グループ</th>
                  <th style={{ minWidth: 200 }}>ルール種別</th>
                  <th style={{ minWidth: 200 }}>ルール名</th>
                  <th style={{ minWidth: 300 }}>条件</th>
                  <th style={{ minWidth: 200 }}>追加ユーザータグ</th>
                  <th style={{ minWidth: 200 }}>除外ユーザータグ</th>
                  <th style={{ minWidth: 200 }}>メモ</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredUserTagRules.map((userTagRule) => {
                  const { id, ref, name, userTagRuleGroupIds, ruleType, conditionEventIds, rows, userTagIds, userTagIdsToRemove, note, } = userTagRule;
                  return (
                    <tr key={id}>
                      <td>
                        {
                          userTagRuleGroupIds?.map(_ => userTagRuleGroupsById[_]?.name).join(', ')
                        }
                      </td>
                      <td>{ruleTypes[ruleType]?.label}</td>
                      <td>{name}</td>
                      <td>
                        {
                          ({
                            surveyAnswer: _ => (
                              <ul className="m-0">
                                {rows.map((rule, i) => {
                                  const { subject, operator, value } = rule;
                                  const displaySubject = {
                                    surveyAnswer: () => {
                                      return questionsById[subject]?.name;
                                    },
                                  }[ruleType]();
                                  const displayValue = {
                                    surveyAnswer: () => {
                                      const question = questionsById[subject];
                                      return {
                                        text: (_) => value,
                                        radio: (_) => value.join(', '),
                                        select: (_) => value.join(', '),
                                        checkbox: (_) => value.join(', '),
                                      }[question?.type]?.();
                                    },
                                  }[ruleType]();
                                  return (
                                    <li key={i} className="small">
                                      <span>「{ellipsis(displaySubject, 15)}」</span>
                                      <span className="ml-1">"{displayValue}"</span>
                                      <span className="ml-1">{operators[operator].label}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            ),
                            entry: _ => (
                              <div className="d-flex gap-1">
                                {
                                  conditionEventIds?.map(_ => <span key={_.id} className="badge badge-secondary">{eventsById[_]?.name}</span>)
                                }
                              </div>
                            ),
                          })[ruleType]?.()
                        }
                      </td>
                      <td>
                        <ul ref={ref}>
                          {(userTagIds || []).map((userTagId) => {
                            const userTag = userTagsById[userTagId];
                            return (
                              userTag != null && (
                                <li key={userTag.id} className="small">
                                  {userTag.name}
                                </li>
                              )
                            );
                          })}
                        </ul>
                      </td>
                      <td>
                        <ul ref={ref}>
                          {(userTagIdsToRemove || []).map((userTagId) => {
                            const userTag = userTagsById[userTagId];
                            return (
                              userTag != null && (
                                <li key={userTag.id} className="small">
                                  {userTag.name}
                                </li>
                              )
                            );
                          })}
                        </ul>
                      </td>
                      <td style={{ whiteSpace: 'pre-line', }}>
                        {note}
                      </td>
                      <td className="text-nowrap text-right">
                        <EditButton
                          itemRef={ref}
                          className="ml-2"
                          FormModal={UserTagRuleFormModal}
                          disabled={!canUpdateUserTagRules(user)}
                          formProps={{ surveyGroups, events, userTags, userTagRuleGroups, }}
                        />
                        <DeleteButton itemRef={ref} className="ml-2" disabled={!canUpdateUserTagRules(user)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div>ユーザータグルールは未登録です</div>
          )}
        </div>
      </div>
    </div>
  );
});

function Groups(props) {
  const { user, userTagRuleGroups = [] } = props;
  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <AddInTenantButton
          itemRef={db.collection('userTagRuleGroups').doc()}
          FormModal={ModelFormModal}
          formProps={{ title: 'グループ追加', fields: groupFields() }}
          disabled={!canUpdateUserTagRules(user)}
        />
      </div>
      <table className="table">
        <thead className="thead-light text-center">
          <tr>
            <th style={{ minWidth: 200 }}>グループ名</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {userTagRuleGroups.map((userTagRuleGroup) => {
            const { id, ref, name } = userTagRuleGroup;
            return (
              <tr key={id}>
                <td>{name}</td>
                <td className="text-nowrap text-right">
                  <EditButton
                    itemRef={ref}
                    className="ml-1"
                    FormModal={ModelFormModal}
                    formProps={{ documentName: 'userTagRuleGroup', title: 'グループ編集', fields: groupFields() }}
                  />
                  <DeleteButton itemRef={ref} className="ml-1" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
